import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Spin } from "antd";
import { MONTHS_LISTS_PASCAL } from "src/entity/common/time.constant";
import { formatNumber } from "src/utils/process-number";
import { EnumReportScope } from "src/entity/main-report/main-report.entity";

interface ColumnChartNormalProps {
  loading: boolean;
  data: ApexAxisChartSeries;
  scope: EnumReportScope;
  selectedYear: string[];
}

const ColumnChartNormal: React.FC<ColumnChartNormalProps> = ({ loading, data, scope, selectedYear }) => {
  const months = scope === EnumReportScope.MONTHLY ? MONTHS_LISTS_PASCAL : selectedYear;
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const isSomeLengthOne = useMemo(() => {
    return data.some((e) => e.data.length === 1);
  }, [data]);
  const isSomeLengthTwo = useMemo(() => {
    return data.some((e) => e.data.length === 2);
  }, [data]);

  // Chart options
  const chartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: {
        // columnWidth: "55%", // Adjust the width of columns
        borderRadius: 4, // Rounded edges for the bars
        columnWidth: "30px", // Set fixed width if there's only one data point
      },
    },
    colors: ["#9ddde1", "red"],
    dataLabels: {
      enabled: true,
      enabledOnSeries: [1],
      textAnchor: "middle",
      distributed: true,
      style: {
        colors: ["red"],
        fontSize: "10px",
      },
      formatter: () => {
        return `o`;
      },
      background: {
        enabled: true,
        foreColor: "red",
        borderRadius: 10,
      },
    },

    stroke: {
      show: true,
      width: [2, 4], // Different line widths for different series
      curve: "straight", // Smooth line for the target
      colors: ["transparent", "#FF4560"], // Bar is transparent, line is red
    },
    xaxis: {
      categories: months,
    },
    yaxis: {
      show: false,
    },
    fill: {
      opacity: [1, 0.5], // Opacity for bar and line
    },
    tooltip: {
      y: {
        formatter: (value) => {
          // Format the number as comma-separated or add units
          return `${formatNumber(value, 2)}`;
        },
      },
    },
    legend: {
      show: true,
      position: "top",
      markers: {
        fillColors: ["#9ddde1", "transparent"], // Set custom colors for the legend markers (1st series: blue, 2nd series: green)
        shape: "square", // Change legend marker shape to square
      },
      formatter: (seriesName, opts) => {
        if (seriesName === "Target") {
          return `<div style="display:flex;">
    <div style="
     position: relative;
            width: 25px; /* Adjust based on circle size */
            height: 15px; /* Height same as circle height */
            display: flex;
            justify-content: center;
            align-items: center;
            ">
        <div style=" width: 12.5px;
            height: 12.5px;
            border-radius: 50%;
            background-color: red;
            position: absolute;
            z-index: 1;"></div>
        <div style="
        width: calc(2.5 * 12.5px); /* 2.5 times the circle's height */
            height: 3px;
            background-color: red;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 0;"></div>
    </div>
    
    <div style="padding-left:5px"><span>${seriesName}</span></div>
   </div>`;
        }
        return seriesName;
      },
    },
  };

  // console.log("[ColumnChart] series : ", data);

  if (!isReady) return null;
  return (
    <div className="w-full">
      {loading || !data.length ? (
        <Spin spinning />
      ) : (
        <>
          <Chart
            options={chartOptions}
            series={data}
            type="line" // Specify type as "line" for the combined chart
            height={350}
          />
        </>
      )}
    </div>
  );
};

export default ColumnChartNormal;
